/**
 * ATTEST-CLASSIFY-BASIS-QUESTIONS-1 — the classify intake questions, per basis.
 *
 * The classify screen used to ask one fixed set of three questions, which was
 * the AT-C 215 set, whatever engagement basis the CPA had chosen above it. Two
 * of those three are AT-C 215 gates and nothing else: `route()` in
 * `core/attest/engagement_router.py` reads
 * `procedures_agreed_by_specified_parties` and `issuer_is_attest_eligible`
 * only on the AUP route. Asking them on a factual-confirmation basis asks the
 * CPA to attest to the conditions of an engagement they explicitly did not
 * choose.
 *
 * The third question — "needs work beyond your own records?" — is gone
 * (ATTEST-BASIS-UNIFY-1). It asked, in two-way form, the same thing the basis
 * selector above it asks in three-way form: what this letter stands on. Two
 * controls for one decision meant a CPA could pick the AUP basis and answer
 * "No", and the engagement type, scope sentence and disclaimers of the
 * resulting letter would disagree about which engagement it was.
 *
 * The earlier version of this module defended keeping it, on the grounds that a
 * Yes on the factual basis is what earned the `LETTER_TYPE_ENGAGEMENT_MISMATCH`
 * refusal, and that removing it would make that refusal unreachable. That was
 * true and is now beside the point: the router derives the engagement type
 * *from* the basis, so the contradiction the refusal caught can no longer be
 * constructed. The refusal is unreachable because the state is, not because the
 * form stopped asking. A CPA who needs AT-C 215 work says so where it belongs —
 * by picking the AUP basis, which is a stricter answer, not a softer one: it
 * turns both remaining questions into hard gates.
 *
 * So: the question set still switches with the *declared basis*, never with a
 * previous answer. Those are different things. The first is asking the right
 * questions; the second is pre-screening the ask, which this surface still does
 * not do.
 */

/**
 * One requested item per line — how a lender's ask actually arrives.
 *
 * Lives beside the form shape it reads rather than in `ClassifyStep`, which is
 * a component: `useLetterCalls` needs this to build the request and has no
 * business importing a screen to get it.
 */
export function splitRequestedItems(text: string): string[] {
  return text.split('\n').map(l => l.trim()).filter(Boolean)
}

export interface ClassifyFormState {
  requestedItemsText: string
  proceduresAgreedBySpecifiedParties: boolean
  issuerIsAttestEligible: boolean
  requestedAssuranceLevel: string
}

export const EMPTY_CLASSIFY_FORM: ClassifyFormState = {
  requestedItemsText: '',
  proceduresAgreedBySpecifiedParties: false,
  issuerIsAttestEligible: true,
  requestedAssuranceLevel: '',
}

/** The yes/no fields of `EngagementRequest`, by their form-state names. */
export type ClassifyBooleanField =
  | 'proceduresAgreedBySpecifiedParties'
  | 'issuerIsAttestEligible'

const BOOLEAN_FIELDS: readonly ClassifyBooleanField[] = [
  'proceduresAgreedBySpecifiedParties',
  'issuerIsAttestEligible',
]

export interface ClassifyQuestion {
  /** Also the `data-testid` and the label's `htmlFor`. */
  id: string
  field: ClassifyBooleanField
  label: string
  help: string
}

/** Engagement-type ids — `core/attest/engagement_router.py`. */
export const AT_C_215_AUP = 'AT_C_215_AUP'
export const SSARS_FACTUAL_CONFIRMATION = 'SSARS_FACTUAL_CONFIRMATION'

const AT_C_215_QUESTIONS: readonly ClassifyQuestion[] = [
  {
    id: 'procedures-agreed',
    field: 'proceduresAgreedBySpecifiedParties',
    label: 'Have the specified parties agreed the procedures?',
    help: 'AT-C 215 requires the parties to agree the procedures and accept their sufficiency before work begins.',
  },
  {
    id: 'issuer-eligible',
    field: 'issuerIsAttestEligible',
    label: 'Is the issuing firm attest-eligible?',
    help: 'Licensed for attest work with a current peer review.',
  },
]

/**
 * Empty, and correctly so (ATTEST-BASIS-UNIFY-1).
 *
 * Both remaining questions are AT-C 215 gates. A factual confirmation states
 * what this firm's own files already show; there are no specified parties to
 * agree procedures with and no attest engagement to be eligible for. The one
 * question that did belong on this basis was the routing question, and the
 * basis selector now asks it. Nothing is left, so this step asks nothing —
 * `ClassifyStep` renders no question block rather than an empty one.
 */
const FACTUAL_CONFIRMATION_QUESTIONS: readonly ClassifyQuestion[] = []

/**
 * The questions to ask for an engagement type.
 *
 * `null` — no letter type chosen yet — gets the AT-C 215 set, which is the
 * superset. The request could still route either way at that point, and an
 * extra question can only produce a refusal that would otherwise be missed;
 * a missing one is what lets an ask through unasked.
 */
export function questionsFor(engagementType: string | null): readonly ClassifyQuestion[] {
  if (engagementType === SSARS_FACTUAL_CONFIRMATION) return FACTUAL_CONFIRMATION_QUESTIONS
  return AT_C_215_QUESTIONS
}

/** One answer, as a patch. A computed key would widen to a string index. */
export function answerPatch(
  field: ClassifyBooleanField,
  value: boolean,
): Partial<ClassifyFormState> {
  const patch: Partial<ClassifyFormState> = {}
  patch[field] = value
  return patch
}

/**
 * The patch that drops answers the new question set does not ask for.
 *
 * Switching basis leaves the old answers in form state, and they would still be
 * posted — so the request would carry "the specified parties agreed the
 * procedures" on a basis where the CPA was never asked and no procedures exist.
 * Recording an attestation nobody made is worse than re-asking. Fields go back
 * to their `EMPTY_CLASSIFY_FORM` value, i.e. what a fresh load would have sent;
 * this never invents a stronger answer than the form's own default.
 *
 * Empty when there is nothing to clear, so the caller can skip a no-op render.
 */
export function clearUnaskedAnswers(
  questions: readonly ClassifyQuestion[],
  form: ClassifyFormState,
): Partial<ClassifyFormState> {
  const asked = new Set<ClassifyBooleanField>(questions.map(q => q.field))
  const patch: Partial<ClassifyFormState> = {}
  for (const field of BOOLEAN_FIELDS) {
    if (asked.has(field)) continue
    if (form[field] === EMPTY_CLASSIFY_FORM[field]) continue
    patch[field] = EMPTY_CLASSIFY_FORM[field]
  }
  return patch
}
